/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("LogPlayerWidget", ["WidgetGroupBase", "WidgetFactory"], function (context, cls) {
  /**
   * Widget used to replay logs from VM, GBC, GDC and so
   * @class LogPlayerWidget
   * @memberOf classes
   * @extends classes.WidgetGroupBase
   */
  cls.LogPlayerWidget = context.oo.Class(cls.WidgetGroupBase, function ($super) {
    return /** @lends classes.LogPlayerWidget.prototype */ {
      __name: "LogPlayerWidget",

      /** @type {Element} **/
      _fileInput: null,
      /** @type {Element} **/
      _delayInput: null,
      /** @type {Element} **/
      _delayFromLogElement: null,
      /** @type {classes.ToggleCheckBoxWidget} **/
      _delayFromLogWidget: null,
      /** @type {Element} **/
      _showHelpersElement: null,
      /** @type {classes.ToggleCheckBoxWidget} **/
      _showHelpersWidget: null,
      /** @type {Element} **/
      _resetButton: null,
      /** @type {Element} **/
      _nextStepButton: null,
      /** @type {Element} **/
      _playAllButton: null,
      /** @type {Element} **/
      _pauseButton: null,
      /** @type {Element} **/
      _forwardInput: null,
      /** @type {Element} **/
      _forwardButton: null,
      /** @type {classes.ToggleCheckBoxWidget} **/
      _statsCheckWidget: null,
      /** @type {Element} **/
      _statsElement: null,
      /** @type {Array} **/
      _log: null,
      /** @type {Map} **/
      _appProcIds: null,
      /** @type {Number} **/
      _currentLogLine: -1,
      /**
       * Track the index of the last processed order
       * @type {Number}
       **/
      _currentDvmOrder: -1,
      /**
       * Number of dvm order to process
       * Process unlimited amount of order if equal to -1
       * @type {Number}
       **/
      _numberOfDvmOrdersToProcess: 0,
      /**
       * Number of order processed since the last
       * play or forward call
       * @type {Number}
       **/
      _processedDvmOrders: 0,
      /** @type {Number} **/
      _startTime: null,
      /** @type {Number} **/
      _maxNumberOfElements: null,
      /** @type {Number} **/
      _maxNumberOfWidgets: null,
      /** @type {Element} **/
      _debugCursorElement: null,
      /** @type {Element} **/
      _keyPressedElement: null,
      /** @type {Element} **/
      _mousePressedElement: null,
      /** @type {Element} **/
      _headerElement: null,
      /** @type {Object} **/
      _gbcBrowserInfo: null,
      /** @type {classes.LogInfoWidget} **/
      _logInfoWidget: null,
      /** @type {Boolean} **/
      _isPaused: false,
      /** @type {Object} **/
      _userInteractionElement: null,
      /** @type {Boolean} **/
      _userInteractionAllowed: false,
      /** @type {classes.ToggleCheckBoxWidget} **/
      _userInteractionWidget: null,

      /** @type {Element} **/
      _customStyle: null,

      /**
       * @inheritDoc
       */
      constructor: function (opts) {
        $super.constructor.call(this, opts);

        window._logplayer = this;

        // Enable Debug Helpers when using logPlayer (even if not in debugMode)
        gbc.classes.DebugHelper.activateDebugHelpers();

        const root = this.getElement();

        this._fileInput = root.querySelector(".logplayer-fileinput");
        cls.DOMElementHelper.on(this._fileInput, "change.LogPlayerWidget", this.loadFile.bind(this));

        this._headerElement = root.querySelector("header");
        this._resetButton = root.querySelector(".logplayer-reset");
        this._nextStepButton = root.querySelector(".logplayer-step");
        this._playAllButton = root.querySelector(".logplayer-play");
        this._pauseButton = root.querySelector(".logplayer-pause");
        this._forwardInput = root.querySelector(".logplayer-forward-count");
        this._forwardButton = root.querySelector(".logplayer-forward");
        this._delayInput = root.querySelector(".logplayer-delay");
        this._delayFromLogElement = root.querySelector(".logplayer-delayfromlog");
        this._statsElement = root.querySelector(".logplayer-stats");
        this._showHelpersElement = root.querySelector(".logplayer-showHelpers");
        this._debugCursorElement = root.querySelector(".logplayer-cursor");
        this._keyPressedElement = root.querySelector(".logplayer-keypressed");
        this._mousePressedElement = root.querySelector(".logplayer-mousepressed");
        this._userInteractionElement = root.querySelector(".logplayer-userInteraction");

        // Default value
        this.setDelayInputValue(500);

        this._gbcTitle = document.querySelector(".mt-toolbar-title");

        this.setButtonEnabled(this._resetButton, false);
        this.setButtonEnabled(this._nextStepButton, false);
        this.setButtonEnabled(this._playAllButton, false);
        this.setButtonEnabled(this._pauseButton, false);
        this.setButtonEnabled(this._forwardButton, false);

        this._statsCheckWidget = cls.WidgetFactory.createWidget("ToggleCheckBox", this.getBuildParameters());
        this._statsCheckWidget.setEnabled(true);
        this._statsCheckWidget.setText(gbc.I18NService.t("gwc.logPlayer.topBar.statistics"));
        this._statsCheckWidget.setTitle(gbc.I18NService.t("gwc.logPlayer.topBar.statisticsTitle"));
        this._statsCheckWidget.setValue(false);
        this._statsElement.appendChild(this._statsCheckWidget.getElement());

        this._delayFromLogWidget = cls.WidgetFactory.createWidget("ToggleCheckBox", this.getBuildParameters());
        this._delayFromLogWidget.setEnabled(true);
        this._delayFromLogWidget.setText(gbc.I18NService.t("gwc.logPlayer.topBar.delayFromLog"));
        this._delayFromLogWidget.setValue(false);
        this._delayFromLogElement.appendChild(this._delayFromLogWidget.getElement());

        this._showHelpersWidget = cls.WidgetFactory.createWidget("ToggleCheckBox", this.getBuildParameters());
        this._showHelpersWidget.setEnabled(true);
        this._showHelpersWidget.setText(gbc.I18NService.t("gwc.logPlayer.topBar.showHelpers"));
        this._showHelpersWidget.setTitle(gbc.I18NService.t("gwc.logPlayer.topBar.showHelpersTitle"));
        this._showHelpersWidget.setValue(true);
        this._showHelpersElement.appendChild(this._showHelpersWidget.getElement());

        if (gbc.DebugService.isActive()) {
          this._userInteractionWidget = cls.WidgetFactory.createWidget("ToggleCheckBox", this.getBuildParameters());
          this._userInteractionWidget.setEnabled(true);
          this._userInteractionWidget.setText(gbc.I18NService.t("gwc.logPlayer.topBar.userInteraction"));
          this._userInteractionWidget.setValue(false);
          this._userInteractionWidget.when(
            context.constants.widgetEvents.valueChanged,
            function () {
              this.addOverlay(!this._userInteractionWidget.getValue(), false, true);
            }.bind(this)
          );
          this._userInteractionElement.appendChild(this._userInteractionWidget.getElement());
        }
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._statsCheckWidget) {
          this._statsCheckWidget.destroy();
          this._statsCheckWidget = null;
        }
        if (this._delayFromLogWidget) {
          this._delayFromLogWidget.destroy();
          this._delayFromLogWidget = null;
          this._delayFromLogElement = null;
        }
        if (this._showHelpersWidget) {
          this._showHelpersWidget.destroy();
          this._showHelpersWidget = null;
        }
        if (this._logInfoWidget) {
          this._logInfoWidget.destroy();
          this._logInfoWidget = null;
        }
        if (this._customStyle) {
          this._customStyle.remove();
        }

        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        // no layout
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        const target = domEvent.target;
        if (cls.DOMElementHelper.isElementOrChildOf(target, this._resetButton)) {
          this.reset();
        } else if (cls.DOMElementHelper.isElementOrChildOf(target, this._nextStepButton)) {
          this.play(1);
        } else if (cls.DOMElementHelper.isElementOrChildOf(target, this._playAllButton)) {
          this.play();
        } else if (cls.DOMElementHelper.isElementOrChildOf(target, this._forwardButton)) {
          this.forward(this._forwardInput.value);
        } else if (cls.DOMElementHelper.isElementOrChildOf(target, this._pauseButton)) {
          this.pause();
        }
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * Define a delay between orders
       * @param {Number} value - value in ms
       */
      setDelayInputValue: function (value) {
        this._delayInput.value = value;
      },

      /**
       * Modify the Delay From Log widget value
       * @param {boolean} value True to use the delay registered with the log.
       */
      setDelayFromLog: function (value) {
        this._delayFromLogWidget.setValue(value);
      },

      /**
       * Define the step to go forward to
       * @param {Number} value - step number to go to
       */
      setForwardInputValue: function (value) {
        this._forwardInput.value = value;
      },

      /**
       * Method called when content is read from a log file
       * @param {String|String[]} content
       */
      loadContent: function (content) {
        this._nextStepButton.removeAttribute("disabled");
        this._playAllButton.removeAttribute("disabled");
        this._log = null;
        this._appProcIds = new Map();
        this.reset();
        const rawContent = content;

        content = content.split("\n").map(function (line) {
          return line.trim();
        });
        if (content.length > 0) {
          if (rawContent.indexOf("gbcLog") < 0 && this._logInfoWidget) {
            this._logInfoWidget.destroy();
          }
          const firstLine = content[0];
          if (firstLine === "LOGVERSION=2") {
            this.parseGdcLog(content);
          } else if (firstLine.substring(0, 5) === "om 0 ") {
            this.parseFglLog(content);
          } else if (firstLine === "guilog v2") {
            this.parseFglLogV2(content);
          } else if (firstLine === "#Version: 1.0") {
            this.parseUaproxyLog(content);
          } else if (rawContent.indexOf("gbcLog") >= 0) {
            this.parseGbcLog(JSON.parse(rawContent));
          }
          this.setButtonEnabled(this._resetButton, true);
          this.setButtonEnabled(this._nextStepButton, true);
          this.setButtonEnabled(this._playAllButton, true);
          this.setButtonEnabled(this._forwardButton, true);
        }
      },

      /**
       * Method called when a file has been inputted
       * @param event
       */
      loadFile: function (event) {
        if (event.target.files.length > 0) {
          context.LogPlayerService.cleanApplications(); //Reset all current applications
          this._fileInput.setAttribute("title", event.target.files[0].name + " loaded");
          this._gbcTitle = document.querySelector(".mt-toolbar-title");
          const titleDiv = document.createElement("div");
          titleDiv.innerHTML = "<b>Log Player : </b> " + event.target.files[0].name;
          context.MainContainerService.setChromeBarMiddleContent(titleDiv);

          const reader = new FileReader();
          reader.onload = function (fEvent) {
            this.loadContent(fEvent.target.result);
          }.bind(this);
          reader.readAsText(event.target.files[0]);
        }
      },

      /**
       * Reset logPlayer at first instruction
       */
      reset: function () {
        // Current app reset
        if (gbc.SessionService.getCurrent()) {
          gbc.SessionService.getCurrent().closeSession();
          gbc.SessionService.getCurrent().destroy();
        }
        if (this._logInfoWidget) {
          this._logInfoWidget.setHidden(false);
        }

        this.setButtonEnabled(this._resetButton, true);
        this.setButtonEnabled(this._nextStepButton, true);
        this.setButtonEnabled(this._playAllButton, true);
        this.setButtonEnabled(this._forwardButton, true);
        this.addOverlay(false, true);

        this._currentLogLine = -1;
        this._currentDvmOrder = -1;
        this._numberOfDvmOrdersToProcess = 0;
        this._processedDvmOrders = 0;

        gbc.LogPlayerService.cleanApplications();
        const appElement = document.querySelector(".gbc_ApplicationWidget");
        if (appElement) {
          appElement.innerHTML = "";
        }
        if (this._gbcTitle) {
          cls.DOMElementHelper.parent(this._gbcTitle, "gbc_ApplicationLauncherWidget").classList.remove("hidden");
        }
        this._cleanDebugHelper();
        this.setButtonEnabled(this._fileInput, true);
      },

      /**
       * Parse a GDC formatted log
       * @param log
       */
      parseGdcLog: function (log) {
        this._log = [];
        for (let i = 0; i < log.length; ++i) {
          if (i !== 0) {
            const line = log[i];
            if (line.length > 0) {
              const appEnd = line.indexOf(":");
              const senderEnd = line.indexOf(":", appEnd + 1);
              const delayEnd = line.indexOf(":", senderEnd + 1);

              const entry = {};
              entry.app = line.substring(0, appEnd);
              entry.sender = line.substring(appEnd + 1, senderEnd);
              entry.delay = line.substring(senderEnd + 1, delayEnd);
              entry.content = context.LogPlayerService.mockOrderResources(line.substring(delayEnd + 1));

              this._log.push(entry);
            }
          }
        }
      },

      /**
       * Parse a GBC formatted Log
       * @param {Object} log - json Formated log
       */
      parseGbcLog: function (log) {
        this._currentLogType = log.logType;
        this._delayFromLogWidget.setValue(false);
        this.setDelayInputValue(5); // if gbcLog: put some small value in this field
        this._log = [];
        let lastKnownTimestamp = 0;
        const style = "color: #007da0"; // console style

        this._gbcBrowserInfo = log.browserInfos;
        // Show info in the page when parsing log
        if (this._logInfoWidget) {
          this._logInfoWidget.destroy();
        }
        this._logInfoWidget = cls.WidgetFactory.createWidget("LogInfo", this.getBuildParameters());
        window.document.querySelector(".gbc_ApplicationLauncherWidget>.containerElement").appendChild(this._logInfoWidget.getElement());
        this._logInfoWidget.setGBCInfo(log.gbcInfos);
        this._logInfoWidget.setBrowserInfo(log.browserInfos);
        this._logInfoWidget.setTheme(log.themeVariables);
        this._logInfoWidget.setSettings(log.storedSettings);

        this._customStyle = document.createElement("style");
        this._customStyle.type = "text/css";
        document.getElementsByTagName("head")[0].appendChild(this._customStyle);

        // Ensure messages are well positioned
        context.HostService.onScreenResize(
          function () {
            const margin = document.body.clientWidth - log.browserInfos.dimension.width;
            this._customStyle.innerHTML = ".gbc_ApplicationHostWidget .mt-centralcontainer{ left: 0px!important}\n";
            this._customStyle.innerHTML += ".gbc_MessageWidget.bottom-right, .gbc_MessageWidget.top-right{ margin-right: " + margin + "px }\n";
          }.bind(this)
        );

        let margin = document.body.clientWidth - log.browserInfos.dimension.width;
        this._customStyle.innerHTML = ".gbc_MessageWidget.bottom-right, .gbc_MessageWidget.top-right{ margin-right: " + margin + "px; }\n";
        // If log specifically said: no sidebar
        if (!log.gbcInfos.isSideBarVisible) {
          const st = document.createElement("style");
          st.type = "text/css";
          st.innerHTML = ".mt-sidebar:not(.mt-sidebar-unavailable){ left: -100%; }";
          document.getElementsByTagName("head")[0].appendChild(st);
          context.MainContainerService.hideSideBarDrawer();
          this._customStyle.innerHTML += ".gbc_ApplicationHostWidget .mt-centralcontainer{ left: 0px!important}";
        }
        // Header part in console
        console.log("%c ~~~~~~~~~~~~~~~~~~~   Starting GBC log   ~~~~~~~~~~~~~~~~~~~", style);
        console.log("%c Recorded date : ", style, new Date(log.runDate));
        console.log(
          "%c GBC " +
            log.gbcInfos.version +
            "-" +
            log.gbcInfos.build +
            " - Platform: " +
            log.gbcInfos.platformName +
            " - Protocol: " +
            log.gbcInfos.protocolType,
          style
        );
        console.log("%c   - Theme: " + log.gbcInfos.activeTheme, style);
        console.log("%c   - Available themes: ", style, log.gbcInfos.availableThemes);
        console.log("%c BROWSER ", style);
        console.log("%c   - User Agent: ", style, log.browserInfos.userAgent);
        console.log("%c   - OS: ", style, log.browserInfos.os);
        console.log("%c   - Language: ", style, log.browserInfos.activeLanguage, log.browserInfos.availableLanguages);
        console.log("%c   - Dimension: ", style, log.browserInfos.dimension);
        console.log("%c   - URL: ", style, log.browserInfos.url);
        console.log("%c THEME VARIABLES: ", style, log.themeVariables);
        console.log("%c STORED SETTINGS: ", style, log.storedSettings);

        // Activate debug mode if log has been recorded with it
        if (log.gbcInfos.debugMode) {
          gbc.DebugService.activate(true);
        }

        let line = null,
          entry = {};

        // Pre-Process every log when parsing
        for (let i = 1; i < log.logs.length; ++i) {
          // start at 1 since 0 is "gbclog"
          line = log.logs[i];
          entry = {};
          entry.delay = 0;
          entry.app = "" + line.appId;

          // Get info about timestamp
          lastKnownTimestamp = line.t ? line.t : lastKnownTimestamp;
          entry.timestamp = lastKnownTimestamp;

          // Timestamp / delay calculation
          if (this._log.length >= 1) {
            if (lastKnownTimestamp !== this._log.at(-1).timestamp) {
              if (this._log.at(-1).timestamp > 0) {
                entry.delay = lastKnownTimestamp - this._log.at(-1).timestamp;
              }
            }
          }

          // In this case, it should be treated as a DVM RESPONSE
          if (line.provider === "[NETWORK]") {
            if (line.httpType === "HTTP RESPONSE") {
              entry.sender = "DVM";
              entry.app = "" + line.appId;
              entry.content = context.LogPlayerService.mockOrderResources(line.data, log.images);
              this._log.push(entry);
            } else if (line.httpType === "HTTP REQUEST") {
              entry.sender = "GBC-NETWORK";
              entry.content = line.type;
              if (line.uaDetails === "auiOrder") {
                entry.content = line.data;
              } else {
                entry.app = "?";
              }

              this._log.push(entry);
            }
          } else if (line.provider === "[MOUSE]") {
            // Add a move entry delay 500ms before the click
            entry.sender = "GBC";
            entry.data = line;
            entry.data.move = true;
            entry.data.click = false;
            const entryClick = JSON.parse(JSON.stringify(entry)); // deep copy the first entry
            entry.data.rightClick = false;
            this._log.push(entry);
            // Add the click entry
            entryClick.data.move = false;
            entryClick.data.click = true;
            this._log.push(entryClick);
          } else {
            entry.sender = "GBC";
            entry.data = line;
            this._log.push(entry);
          }
        }
      },

      /**
       * Parse a FGL formatted log
       * @param log
       */
      parseFglLog: function (log) {
        this._log = [];
        for (const line of log) {
          if (line.length > 0) {
            const entry = {};
            entry.app = "0";
            entry.delay = 1000;
            if (line[0] === "#") {
              if (line.substr(0, 7) === "#event ") {
                entry.sender = "FE";
              } else {
                entry.sender = "COMMENT";
              }
              entry.content = context.LogPlayerService.mockOrderResources(line.substring(1));
            } else {
              entry.sender = "DVM";
              entry.content = context.LogPlayerService.mockOrderResources(line);
            }
            this._log.push(entry);
          }
        }
      },

      /**
       * Parse a FGL with new log format
       * @param log
       */
      parseFglLogV2: function (log) {
        this._log = [];
        let lastOrderTime = 0;
        for (let i = 1; i < log.length; ++i) {
          const line = log[i];
          if (line.length > 0) {
            const colonIndex = line.indexOf(":");
            const info = line.substr(0, colonIndex).split(" ");
            const entry = {};
            entry.app = info[0];
            const timestamp = Number.parseInt(info[1]);
            entry.delay = timestamp - lastOrderTime;
            lastOrderTime = timestamp;
            entry.sender = info[2] === "i" ? "FE" : "DVM";
            entry.content = context.LogPlayerService.mockOrderResources(line.substr(colonIndex + 1));
            this._log.push(entry);
          }
        }
      },

      /**
       * Parse UA proxy formatted log
       * @param log
       */
      parseUaproxyLog: function (log) {
        this._log = [];
        let fieldsCount = null;
        let relativeTimeFieldIndex = null;
        let contextsFieldIndex = null;
        let eventTypeFieldIndex = null;
        let eventParamsFieldIndex = null;
        let timeSinceLastDvmOrder = 0;

        for (const line of log) {
          if (line.length !== 0) {
            if (line[0] === "#") {
              if (line.substr(0, 9) === "#Fields: ") {
                const fields = line.substr(9).split(" ");
                fieldsCount = fields.length;
                relativeTimeFieldIndex = fields.indexOf("relative-time");
                contextsFieldIndex = fields.indexOf("contexts");
                eventTypeFieldIndex = fields.indexOf("event-type");
                eventParamsFieldIndex = fields.indexOf("event-params");
                if (relativeTimeFieldIndex === -1 || contextsFieldIndex === -1 || eventTypeFieldIndex === -1 || eventParamsFieldIndex === -1) {
                  this.addLog(" ", '<b>Log fields missing. Enable "ALL DEBUG" categories</b>');
                  return;
                }
              }
            } else {
              let sectionStart = -1;
              const sections = [];
              let j = 0;
              while (j < line.length) {
                const c = line[j];
                if (c === " ") {
                  sections.push(line.substring(sectionStart + 1, j));
                  sectionStart = j;
                } else if (c === '"') {
                  j = line.indexOf('"', j + 1);
                }
                if (sections.length === fieldsCount - 1) {
                  sections.push(line.substr(j + 1));
                  break;
                }
                ++j;
              }
              timeSinceLastDvmOrder += Number.parseFloat(sections[relativeTimeFieldIndex]);
              const contexts = sections[contextsFieldIndex];
              const eventType = sections[eventTypeFieldIndex];
              const eventParams = sections[eventParamsFieldIndex];
              const vmIdIndex = contexts.indexOf("VM=");
              let vmId = null;
              if (vmIdIndex !== -1) {
                let semiColonIndex = contexts.indexOf(";", vmIdIndex);
                if (semiColonIndex === -1) {
                  semiColonIndex = undefined;
                }
                vmId = contexts.substring(vmIdIndex + 3, semiColonIndex);
              }
              if (vmId !== null) {
                if (eventType === '"Sending VM"' || eventType === '"Sending UA"') {
                  if (eventParams.indexOf("meta Connection") === 0 || eventParams.indexOf("om") === 0 || eventParams.indexOf("event _om") === 0) {
                    const sender = eventType === '"Sending UA"' ? "DVM" : "FE";
                    let orders = eventParams;
                    let crIndex = -1;
                    do {
                      crIndex = orders.indexOf("\\n", crIndex + 1);
                      if (crIndex > 1 && orders[crIndex - 1] !== "\\") {
                        orders = orders.substr(0, crIndex) + "\n" + orders.substr(crIndex + 2);
                      }
                    } while (crIndex !== -1);
                    let order = this.detachString(orders);
                    order = order.replaceAll(/([^\\])\\t/g, "$1\t");
                    order = order.replaceAll(/\\\\n/g, "\\n");
                    order = order.replaceAll(/\\\\\\\\/g, "//");
                    order = order.replaceAll(/\\\\"/g, '\\"');
                    order = order.replaceAll(/\\r/g, "");
                    order = order.replaceAll(/%/g, "%25");
                    order = decodeURIComponent(order.replaceAll(/\\x/g, "%"));
                    order = context.LogPlayerService.mockOrderResources(order);

                    if (order !== "-") {
                      // '-' marks the DVM deconection
                      const entry = {};
                      entry.app = this.detachString(vmId);
                      entry.sender = sender;
                      entry.content = order;
                      entry.delay = sender === "DVM" ? Math.round(timeSinceLastDvmOrder * 1000) : 0;
                      timeSinceLastDvmOrder = 0;
                      this._log.push(entry);
                    }
                  } else if (/X-FourJs-Closed: true/.test(line)) {
                    const closeEntry = {};
                    closeEntry.app = this.detachString(vmId);
                    closeEntry.sender = "";
                    closeEntry.content = "";
                    closeEntry.close = true;
                    closeEntry.delay = 0;
                    timeSinceLastDvmOrder = 0;
                    this._log.push(closeEntry);
                  }
                }
              }
            }
          }
        }
      },

      /**
       * Play a defined amount of steps, from the current step
       * @param stepCount Amount of steps to play. Play the whole log if sets to 0. (Default is 0)
       */
      play: function (stepCount = 0) {
        if (!this._log || stepCount < 0) {
          return;
        }
        this._startTime = performance.now();
        this._processedDvmOrders = 0;
        this._numberOfDvmOrdersToProcess = stepCount || -1;
        this.run();
      },

      /**
       * Pause the log execution
       */
      pause: function () {
        this._isPaused = true;
        this.setButtonEnabled(this._playAllButton, true);
        this.setButtonEnabled(this._pauseButton, false);
        this.setButtonEnabled(this._nextStepButton, true);
        this.setButtonEnabled(this._resetButton, true);
        this.addOverlay(false);
      },

      /**
       * Play until the step is reached
       * @param {number} stepToReach Step to reach. The step will be played
       */
      forward: function (stepToReach) {
        if (!this._log) {
          return;
        }

        const numberOfStepToProcess = stepToReach - this._currentDvmOrder;
        if (numberOfStepToProcess < 1) {
          return;
        }
        this._processedDvmOrders = 0;
        this._numberOfDvmOrdersToProcess = numberOfStepToProcess;
        this.run();
      },

      /**
       * Rework an entry to add some extra info in case of gbcLog
       * @param {Object} entry - log entry to re-format
       * @return {Object} return the modified entry
       * @private
       */
      _handleGBCLog: function (entry) {
        const entryData = entry.data;
        if (entryData) {
          // get category
          const mouseMatch = entryData.provider.match(/\[MOUSE\]/);
          const keyboardMatch = entryData.provider.match(/\[KEYBOARD\]/);
          const uiMatch = entryData.provider.match(/\[UI\]/);
          let outstr = "";

          // Anything MOUSE related
          if (mouseMatch) {
            const x = entryData.clientX - 16; // 16px is the half of the cursor image
            const y = entryData.clientY + this._headerElement.clientHeight - 16;

            let action = entryData.click ? "click" : "move";
            action = entryData.rightClick ? "rightClick" : action;

            outstr = action + " at x:" + x + " y:" + y;
            console.log("%c " + outstr + " on", "color: #007da0", entryData.itemId, entryData.auiId, entryData.itemElement);

            const clickDelay = action === "click" ? entry.delay : 100;
            entry.delay = action === "move" ? 0 : entry.delay;

            this.setDebugMouse(x, y, action, clickDelay);
            // Anything KEYBOARD related
          } else if (keyboardMatch) {
            outstr = "Keyboard key pressed: ";
            console.log("%c " + outstr, "color: #007da0", entryData.eventKey || entryData.bufferedKey);
            if (entryData.bufferedKey && entryData.eventKey && entryData.type === "onKeyDown") {
              this.setDebugKeyboardKey(entryData.bufferedKey);
            }
            // Anything UI related
          } else if (uiMatch) {
            if (entryData.type === "sidebar") {
              if (entryData.status) {
                context.MainContainerService.showSideBarDrawer();
              } else {
                context.MainContainerService.hideSideBarDrawer();
              }
            } else if (entryData.type === "dropdown") {
              if (entryData.name === "ChromeRightBarWidget") {
                console.log("chromebar open");
                if (entryData.status) {
                  context.ChromeActionService.openRightFloatingPanel();
                } else {
                  context.MainContainerService.hideFloatingPanel();
                }
              } else if (entryData.name === "ContextMenuWidget") {
                if (entryData.data.parentName === "TableColumnTitleWidget") {
                  const tbColumn = window.gbcWidget(entryData.data.auiTag);
                  const titleWidget = tbColumn.getTitleWidget();
                  if (entryData.status) {
                    titleWidget.showContextMenu(entryData.data.x);
                  } else if (titleWidget._contextMenu) {
                    titleWidget._contextMenu.hide();
                  }
                }
              }
            }
          }
        }
        if (entry.content && entry.content.substr(0, 4) === "om 0") {
          entry.dimension = this._gbcBrowserInfo.dimension;
        }
        return entry;
      },

      /**
       * Run the Log player
       */
      run: function () {
        this.addOverlay(true);
        // Set the control button accordingly
        if (!this._isPaused) {
          this.setButtonEnabled(this._resetButton, false);
        }
        this.setButtonEnabled(this._nextStepButton, this._isPaused);
        this.setButtonEnabled(this._playAllButton, this._isPaused);
        this.setButtonEnabled(this._pauseButton, !this._isPaused);
        this.setButtonEnabled(this._forwardButton, false);

        // Prevent file upload while processing
        this.setButtonEnabled(this._fileInput, false);

        context.LogPlayerService.setLogReplay(true);
        // While there are instruction to process
        while (this._currentLogLine < this._log.length - 1) {
          // If there is errors, raise an event
          if (window.__jsErrors && window.__jsErrors.length) {
            window.critical.display(window.__jsErrors[0]);
            this.emit(context.constants.widgetEvents.logPlayerError);
            throw window.__jsErrors[0];
          }
          // If the log player is paused, do nothing
          if (this._isPaused) {
            this._isPaused = false;
            return;
          }
          // Read the line of the log
          this._currentLogLine += 1;
          let entry = this._log[this._currentLogLine];
          const isDvm = entry.sender === "DVM" && entry.content.substr(0, 5) !== "meta ";
          const isDVMmetaClient = entry.sender === "DVM" && entry.content.substr(0, 15) === "meta Connection";
          const isOm0 = entry.sender === "DVM" && entry.content.substr(0, 4) === "om 0";
          const om0Content = isOm0 ? cls.AuiProtocolReader.translate(entry.content) : null;
          let app = context.LogPlayerService.getApplication(
            0,
            entry.app,
            isOm0 ? om0Content[0].operations[0].node.attributes.procId : null,
            entry.sender !== "DVM"
          );
          if (this._currentLogType === "gbcLog") {
            entry = this._handleGBCLog(entry);
          }
          if (app) {
            if (isDVMmetaClient) {
              //Application has already received meta connection info
              if (app.info() && app.info().procId) {
                //A new application is starting with the same id must remove the existing one
                context.LogPlayerService.removeApplication(0, entry.app);

                app = context.LogPlayerService.getApplication(
                  0,
                  entry.app,
                  isOm0 ? om0Content[0].operations[0].node.attributes.procId : null,
                  entry.sender !== "DVM"
                );
              }

              const msg = cls.AuiProtocolReader.translate(entry.content);
              this._appProcIds.set(entry.app, msg[0].attributes.procId);
              app.info().procId = msg[0].attributes.procId;
              app.info().connectionInfo = msg[0].attributes;
              app.getSession().getNavigationManager().updateApplicationInformation(app);
            } else if (isOm0 && !app.info().connectionInfo) {
              const msg = cls.AuiProtocolReader.translate(entry.content);
              app.info().procId = msg[0].operations[0].node.attributes.procId;
              app.info().connectionInfo = {
                procId: msg[0].operations[0].node.attributes.procId
              };
              app.getSession().getNavigationManager().updateApplicationInformation(app);
            }
          }
          // Handle delay from log if option is activated
          if (!this._delayFromLogWidget.getValue()) {
            entry.delay = isDvm ? Number.parseInt(this._delayInput.value, 10) : 5;
          }
          if (entry.content) {
            this.addLog(entry.app + ":" + entry.sender, entry.content, isDvm);
            if (isDvm && entry.content !== "filetransfer") {
              this._processedDvmOrders += 1;
              this._currentDvmOrder += 1;
              this.setForwardInputValue(this._currentDvmOrder);

              let callback = null;
              // If didn't we processed the expected number of orders
              // or if we should run until the end of the log
              // Call run method again after a small delay
              if (this._processedDvmOrders < this._numberOfDvmOrdersToProcess || this._numberOfDvmOrdersToProcess === -1) {
                callback = this._delayRunner(entry.delay);
              } else {
                // End of the log or wanted order where processed
                callback = this._setPlayerEnabled.bind(this);
              }

              context.LogPlayerService.setVisibleApplication(app);
              app.dvm.manageAuiOrders(entry.content, callback);
              // Entry provides a dimension: use it to change the app width!
              if (entry.dimension) {
                document.querySelector(".gbc_ApplicationWidget").style.width = entry.dimension.width + "px";
                document.querySelector(".gbc_ApplicationWidget").style.height = entry.dimension.height + "px";
                document.querySelector(".gbc_SessionWidget").style.overflow = "auto"; // allow scrolling
              }
              this.emit(context.constants.widgetEvents.logStepDone, this._processedDvmOrders);
              break;
            } else if (entry.close) {
              const closeApp = context.LogPlayerService.getApplication(0, entry.app);
              if (closeApp) {
                closeApp.setEnding();
                closeApp.stop();
                context.LogPlayerService.removeApplication(0, entry.app);
              }
            } else {
              this._delayRunner(entry.delay)();
              break;
            }
          } else if (entry.data && entry.data.click && entry.data.auiId) {
            const widget = window.gbcWidget(entry.data.auiId);
            if (widget && widget instanceof cls.PageWidget) {
              const pageTitleWidget = widget.getTitleWidget();
              const domEvent = {
                clientX: entry.data.clientX,
                clientY: entry.data.clientY,
                target: pageTitleWidget.getElement()
              };
              this._registerTimeout(
                function () {
                  context.MouseService._onClick(domEvent, null, false);
                }.bind(this),
                entry.delay
              );
            }
            this._delayRunner(entry.delay)();
            break;
          } else {
            this._delayRunner(entry.delay)();
            break; // no entry
          }
        }

        // Update the statistics widget
        if (this._statsCheckWidget.getValue()) {
          const elementNumber = document.getElementsByTagName("*").length;
          if (this._maxNumberOfElements === null || elementNumber > this._maxNumberOfElements) {
            this._maxNumberOfElements = elementNumber;
          }
          const widgetNumber = gbc.WidgetService.getAllWidgetsCount();
          if (this._maxNumberOfWidgets === null || widgetNumber > this._maxNumberOfWidgets) {
            this._maxNumberOfWidgets = widgetNumber;
          }
        }

        // End of the log
        const logIsEnded = this._currentLogLine >= this._log.length - 1;
        if (logIsEnded) {
          // Generate the log report
          let reportTime = "";
          if (this._startTime !== null) {
            const t1 = performance.now();
            reportTime = "Duration: " + Number.parseInt(t1 - this._startTime) + " milliseconds.";
            this.addLog(" ", reportTime);
            this._startTime = null;
          }
          let reportDOM = "";
          if (this._maxNumberOfElements !== null) {
            reportDOM = "Max number of DOM elements: " + this._maxNumberOfElements;
            this.addLog(" ", reportDOM);
            this._maxNumberOfElements = null;
          }
          if (this._maxNumberOfWidgets !== null) {
            this.addLog(" ", reportDOM);
            this._maxNumberOfWidgets = null;
          }

          // End of log, set the button accordingly
          this.setButtonEnabled(this._resetButton, true);
          this.setButtonEnabled(this._nextStepButton, false);
          this.setButtonEnabled(this._playAllButton, false);
          this.setButtonEnabled(this._pauseButton, false);
          this.setButtonEnabled(this._forwardButton, false);
          this.setButtonEnabled(this._fileInput, true);
          this.log = [];

          let session = context.SessionService.getCurrent();
          if (session) {
            session.closeSession();
          }
          this.addLog(" ", "<b>== LOG FINISHED ==</b>");
          context.LogPlayerService.cleanApplications();
          context.LogPlayerService.setLogReplay(false);
          this._cleanDebugHelper();
        }
        const orderWasProcessed = this._processedDvmOrders >= this._numberOfDvmOrdersToProcess && this._numberOfDvmOrdersToProcess !== -1;
        if (logIsEnded || orderWasProcessed) {
          this.emit(context.constants.widgetEvents.ready);
        }
      },

      /**
       * Wrapper to delay the logPlayer instructions
       * @param {Number} delay - in ms
       * @return {function} callback
       * @private
       */
      _delayRunner: function (delay) {
        return function () {
          this._registerTimeout(
            function () {
              this.run();
            }.bind(this),
            delay || this._delayInput.value
          );
        }.bind(this);
      },

      /**
       * Set player enabled
       * @private
       */
      _setPlayerEnabled: function () {
        this.setButtonEnabled(this._resetButton, true);
        this.setButtonEnabled(this._nextStepButton, true);
        this.setButtonEnabled(this._playAllButton, true);
        this.setButtonEnabled(this._pauseButton, false);
        this.setButtonEnabled(this._forwardButton, true);
        this.setButtonEnabled(this._fileInput, false);
      },

      /**
       * Display a log in the console
       * @param context
       * @param content
       * @param withOrderNumber
       */
      addLog: function (context, content, withOrderNumber) {
        const orderNr = withOrderNumber ? this._currentDvmOrder : " ";
        console.log("LOGPLAYER", orderNr, context, content);
      },

      /**
       * Change the state of a given button
       * @param {Element} button - button element to change
       * @param {Boolean} enabled - true to activate the button, false otherwise
       */
      setButtonEnabled: function (button, enabled) {
        cls.DOMElementHelper.toggleClasses(button, !enabled, "disabled");
        if (enabled) {
          button.removeAttribute("disabled");
          if (button === this._fileInput) {
            this._fileInput.querySelector("input").removeAttribute("disabled");
          }
        } else {
          button.setAttribute("disabled", "disabled");
          if (button === this._fileInput) {
            this._fileInput.querySelector("input").setAttribute("disabled", "disabled");
          }
        }
      },

      /**
       * Detaches the huge uaproxy log string from chunks we really need for memory optimi
       * @param {string} s
       * @return {string}
       */
      detachString: function (s) {
        return (" " + s).slice(1);
      },

      /**
       * Method to put a visual mouse pointer at a given position
       * @param {Number|Boolean} x - horizontal position or false to hide it
       * @param {Number?} y - vertical position
       * @param {String?} action - click or move
       * @param {Number?} clickDelay - delay the click effect (in ms)
       */
      setDebugMouse: function (x, y, action, clickDelay) {
        if (this._showHelpersWidget.getValue()) {
          this._cleanDebugHelper();
          if (x && y) {
            this._debugCursorElement.classList.remove("hidden");
            if (action === "move") {
              this._debugCursorElement.style.top = y + "px";
              this._debugCursorElement.style.left = x + "px";
            }
            if (action.toLowerCase().indexOf("click") >= 0) {
              this._mousePressedElement.classList.remove("hidden");
              const left = this._mousePressedElement.querySelector(".key-left");
              const right = this._mousePressedElement.querySelector(".key-right");

              this._registerTimeout(
                function () {
                  this._debugCursorElement.classList.add("highlight");
                  if (action === "click") {
                    left.classList.add("pressed");
                  } else if (action === "rightClick") {
                    right.classList.add("pressed");
                  }
                  this._registerTimeout(
                    function () {
                      this._debugCursorElement.classList.remove("highlight");
                      left.classList.remove("pressed");
                      right.classList.remove("pressed");
                    }.bind(this),
                    500
                  );
                }.bind(this),
                clickDelay
              );
            }
          } else {
            this._mousePressedElement.classList.add("hidden");
            this._debugCursorElement.classList.add("hidden");
          }
        }
      },

      /**
       * Method to add a visual info about pressed key
       * @param {String} key - key to display
       */
      setDebugKeyboardKey: function (key) {
        if (this._showHelpersWidget.getValue()) {
          this._cleanDebugHelper();
          this._keyPressedElement.classList.remove("hidden");
          const keyletter = this._keyPressedElement.querySelector(".key-letter");
          const keymod = this._keyPressedElement.querySelector(".key-modifier");

          const keyMatch = key.match(/(ctrl|alt|mod|shift)*\+*(.*)/);

          if (keyMatch[1]) {
            // has modifier
            keymod.innerHTML = "<span>" + keyMatch[1] + "</span>";
            keymod.classList.add("pressed");
            keymod.classList.remove("hidden");
            key = keyMatch[2];
          } else {
            key = keyMatch[2];
            keymod.classList.add("hidden");
          }
          switch (key) {
            case "up":
              key = "⬆";
              break;
            case "down":
              key = "⬇";
              break;
            case "left":
              key = "⬅";
              break;
            case "right":
              key = "⮕";
              break;
            default:
              break;
          }

          keyletter.innerHTML = "<span>" + key + "</span>";
          keyletter.classList.add("pressed");

          this._registerTimeout(function () {
            keymod.classList.remove("pressed");
            keyletter.classList.remove("pressed");
          }, 500);
        }
      },

      /**
       * Clear screen of mouse and keyboard info
       * @private
       */
      _cleanDebugHelper: function () {
        this._keyPressedElement.classList.add("hidden");
        this._debugCursorElement.classList.add("hidden");
        this._mousePressedElement.classList.add("hidden");
      },

      /**
       * Add / remove an overlay to prevent any user interaction while playing a log
       * @param {Boolean} overlay - true to prevent user interaction, false otherwise
       * @param {Boolean} [force] - true to force the given value without check
       */
      addOverlay: function (overlay, force) {
        // Only if overlay status is not forced
        if (!force) {
          if (this._userInteractionWidget) {
            overlay = !this._userInteractionWidget.getValue();
          } else {
            overlay = true;
          }
        }
        gbc.OverlayService.setCursor("logplayer", "not-allowed");

        this.toggleClasses(overlay, "running");
        if (overlay) {
          gbc.OverlayService.enable("logplayer", this.getContainerElement());
        } else {
          gbc.OverlayService.disable("logplayer");
        }
      }
    };
  });
  cls.WidgetFactory.registerBuilder("LogPlayer", cls.LogPlayerWidget);
});
